import { IconButton } from '@/components/buttons/Buttons';
import EmptyTable from '@/components/empty/EmptyTable';
import AutoCompleteSearch from '@/components/search-bar/AutocompleteSearchBar';
import { Table } from '@/components/table/Tables';
import { routeWithId } from '@/constants/routes';
import ConfirmationDialogHeader from '@/modules/confirmation/components/ConfirmationDialogHeader';
import { showConfirmationDialog } from '@/modules/confirmation/engines/confirmation.library';
import { showError, showNotification } from '@/modules/notification/engine/notification.library';
import { format, formatDistanceToNowStrict } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import Papa from 'papaparse';
import { Column } from 'primereact/column';
import type { DataTableRowClickEvent, DataTableValue } from 'primereact/datatable';
import { Dropdown } from 'primereact/dropdown';
import { Toolbar } from 'primereact/toolbar';
import { useEffect, useState } from 'react';
import { MdCloudDownload, MdCloudUpload, MdDelete, MdEdit, MdPersonAdd, MdRestartAlt } from 'react-icons/md';
import type { X4bEmployeeListItem } from '../engine/people.types';
import AddPeopleScreen from '../views/AddPeopleScreen';
import StatusTag from './StatusTag';
import { GenericResponse, ListResponse, showTypedModal } from '@/common/common_utils';
import { axiosInstance } from '@/services/axios.service';
import { ENDPOINTS } from '@/constants/endpoints';
import { useQuery } from '@tanstack/react-query';
import { BaseResponse, getEmployeeStatusString, X4bEmployeeStatus } from '@/types';
import NiceModal from '@ebay/nice-modal-react';
import ConfirmModal from './ConfirmModal';
import { useTableState } from '@/common/useTableState';
import { Row } from '@/components/container/Container';
import { Button } from 'primereact/button';
import EditPeopleModal from '../views/EditPeopleScreen';
import { useAuthenticationStore } from '@/store';
import { X4bUserRoleEnum } from '@/constants/roles';

type AddType = 'single' | 'bulk';

type Option = { name: string; code: string; };
const defaultStatusOptions: Option[] = [
	{ name: 'All', code: 'All' },
	{ name: 'Active', code: 'Active' },
	{ name: 'Invited', code: 'Invited' },
	{ name: 'Admin', code: 'Admin' },
];

const ApprovedPeopleTable = () => {
	const [selectedPeoples, setSelectedPeoples] = useState<X4bEmployeeListItem[]>([]);
	const [addType, setAddType] = useState<AddType>('single');
	const [editType, setEditType] = useState<AddType>('single');
	const [openAddModal, setOpenAddModal] = useState(false);
	const isManagerOrAdmin = useAuthenticationStore((state) => state.hasRoles)([X4bUserRoleEnum.Admin, X4bUserRoleEnum.Manager]);

	const [search, setSearch] = useState<string>('');

	const [statusOptions, setStatusOptions] = useState<Option[]>(defaultStatusOptions);
	const { sort, filter, updateFilter, tableProps } = useTableState({ skip: 0, take: 5, nameEmailFilter: '', statusFilter: defaultStatusOptions[0] });

	const filterKey = { ...filter, statusFilter: (({ name, ...rest }) => rest)(filter.statusFilter) };

	const { data: people, isFetching, refetch } = useQuery({
		queryKey: ['ApprovedPeopleTable.GetEmployeeList', filterKey, sort],
		initialData: { Items: [] as X4bEmployeeListItem[], TotalRecords: 0 },
		queryFn: async () => {
			const result = await axiosInstance.post<GenericResponse<ListResponse<X4bEmployeeListItem>>>(
				ENDPOINTS['/api/X4bEmployee/GetEmployeeList'], {
				Skip: filter.skip,
				Take: filter.take,
				SortField: sort.sortField,
				SortOrder: sort.sortOrder,
				Filters: [
					{ Name: "Status", Value: filter.statusFilter.code == "All" ? "" : filter.statusFilter.code },
					{ Name: "NameEmail", Value: filter.nameEmailFilter }
				],
			}
			);
			return result.data.Data;
		}
	});

	useEffect(() => {
		if (!isFetching && people) {
			setStatusOptions(defaultStatusOptions.map(option => {
				if (option.code !== filter.statusFilter.code) return option;
				return { code: option.code, name: `${option.name} (${people.TotalRecords})` };
			}));

			updateFilter({
				statusFilter: {
					code: filter.statusFilter.code,
					name: `${defaultStatusOptions.find(o => o.code === filter.statusFilter.code)?.name ?? filter.statusFilter.name} (${people.TotalRecords})`,
				}
			});
		}
	}, [people, isFetching]);

	const navigate1 = useNavigate();

	const navigate = (event: DataTableRowClickEvent) => {
		navigate1(routeWithId('/people/:id', event.data.Id));
	};

	const pendingPeople: X4bEmployeeListItem[] = [];
	// TODO
	// const pendingPeople = selectedPeoples.filter((pep) => pep.StatusName === 'Pending');

	const reminderType = pendingPeople && pendingPeople.length > 1 ? 'bulk' : 'single';
	const hasPending = pendingPeople.length >= 1;
	const hasPeople = people.Items.length > 0;
	const hasEdit = selectedPeoples.length === 1;

	const sendReminderHeaderTemplate = (
		<ConfirmationDialogHeader
			message={'Resend Reminders?'}
			severity="warning"
		/>
	);

	const sendReminderMessage = (
		<div>
			{reminderType === 'single' ? (
				<p>
					Are you sure you want to resend the invitation reminder to{' '}
					{selectedPeoples[0]?.FullName}?
				</p>
			) : (
				<p>
					Are you sure you want to resend the invitation reminder to
					the selected employees?
				</p>
			)}
			<p className="mt-8">
				This action will send another email reminder prompting them to
				join Para Xpress's account on Xpress for Business.
			</p>
		</div>
	);

	const handleSendReminder = () =>
		showConfirmationDialog(sendReminderMessage, {
			accept: () => {
				showNotification('Reminder resent successfully');
			},
			acceptLabel: 'Resend Reminders',
			rejectLabel: 'Cancel',
			header: sendReminderHeaderTemplate,
			rejectClassName: 'people-back-button',
			className: 'people-send-reminder-modal',
		});

	const downloadPeople = async (people: X4bEmployeeListItem[]): Promise<void> => {
		const unparseAsync = (jsonData: X4bEmployeeListItem[]): Promise<string> => {
			return new Promise((resolve, reject) => {
				try {
					const csv = Papa.unparse(jsonData);
					resolve(csv);
				} catch (error) {
					reject(error);
				}
			});
		};

		const currentTimestamp = format(new Date(), 'yyyy-MM-dd__HH-mm');
		const filename = `Xpress_People_${currentTimestamp}.csv`;

		if (!Array.isArray(people) || !people.length) {
			showError('No data to export.');
			return;
		}

		try {
			const csv = await unparseAsync(people);
			const blob = new Blob([csv], { type: 'text/csv' });
			const url = URL.createObjectURL(blob);
			const a = document.createElement('a');
			a.href = url;
			a.download = filename;
			a.click();
			a.remove();
			URL.revokeObjectURL(url);
		} catch (error) {
			showError(`Error converting to CSV:${error}`);
		}
	};

	const handleEditPeople = async () => {
		selectedPeoples.length > 1 ? setEditType('bulk') : setEditType('single');

		await showTypedModal(EditPeopleModal, { type: editType, employeeId: selectedPeoples[0]?.Id });
		await refetch();
	};

	const openDeleteModal = async () => {
		await NiceModal.show(ConfirmModal, {
			title: selectedPeoples && selectedPeoples.length === 1
				? `Delete ${selectedPeoples[0].FullName}?`
				: 'Delete selected employees?',
			message: selectedPeoples && selectedPeoples.length > 1
				? 'Are you sure you want to delete these employees? This action cannot be undone.'
				: 'Are you sure you want to delete this employee? This action cannot be undone.',
			acceptLabel: 'Delete',
			rejectLabel: 'Cancel',
			onAccept: async () => {
				await axiosInstance.post<BaseResponse>(ENDPOINTS['/api/X4bEmployee/DeleteEmployees'], selectedPeoples.map(i => i.Id));
				await refetch();
				showNotification(deletePeopleBannerMessage());
			}
		})
	}

	const deletePeopleBannerMessage = () => {
		let message = '';

		if (selectedPeoples.length) {
			if (selectedPeoples.length > 1) {
				const filteredPeoples = selectedPeoples.filter(
					(pep) => pep.RoleName === 'Admin',
				);
				if (filteredPeoples.length) {
					message =
						'Cannot delete some employees because they have admin access. Please edit their role first before attempting to delete them.';
				} else {
					message = 'Selected employees deleted successfully,';
				}
			} else {
				const selectedPeople = selectedPeoples[0];
				if (selectedPeople.RoleName === 'Admin') {
					message = `${selectedPeople.FullName} has administrator access. Please remove their admin access before attempting to delete them.`;
				} else {
					message = `${selectedPeople.FullName} has been deleted.`;
				}
			}
		}

		return message;
	};

	const searchFilter = (user: X4bEmployeeListItem, query: string) =>
		user.FullName.toLowerCase().includes(query.toLowerCase()) ||
		user.Email.toLowerCase().includes(query.toLowerCase());

	// the UX is very strange and seems inconsistent with usual ways of working with autocomplete and tables. I suspect design error.
	const handleSearchSelect = (user: X4bEmployeeListItem) => updateFilter({ nameEmailFilter: user.Email });
	const handleSearchChange = (e: string) => {
		updateFilter({ nameEmailFilter: e });
		setSearch(e);
	};

	// Status Badge Template
	const statusBodyTemplate = (rowData: X4bEmployeeListItem) => (
		<StatusTag
			value={getEmployeeStatusString(rowData.Status)}
			severity={`${rowData.Status === X4bEmployeeStatus.Active ? 'success' : 'warning'}`}
		/>
	);

	const startContent = (
		<div className="flex gap-6 tablet:w-full">
			<Dropdown
				value={filter.statusFilter}
				onChange={(e) => updateFilter({ statusFilter: e.value })}
				options={statusOptions}
				className="w-full md:min-w-44 h-14 tablet:!w-auto tablet:!flex-none"
				optionLabel="name"
				pt={{
					root: {
						className: 'people-dropdown',
					},
					panel: {
						className: 'people-dropdown-content',
					},
				}}
			/>
			<AutoCompleteSearch<X4bEmployeeListItem>
				suggestions={people.Items}
				placeholder="Search by name or email"
				onSelect={handleSearchSelect}
				onChange={handleSearchChange}
				filterMethod={searchFilter}
				label="FullName"
				description="Email"
				value={search}
				className="md:w-96 tablet:!w-full tablet:!min-w-0"
			/>
		</div>
	);

	const selectedActionButtons = hasPending ? (
		<div className="flex">
			<IconButton
				id="people-send-notification"
				label="Resend Reminder"
				icon={<MdRestartAlt className="mr-2" />}
				className="secondary h-10"
				rounded
				onClick={handleSendReminder}
			/>
		</div>
	) : (
		<div className="flex gap-3">
			{hasEdit && (
				<IconButton
					id="people-edit"
					label="Edit"
					icon={<MdEdit className="mr-2" />}
					className="secondary h-10"
					rounded
					onClick={handleEditPeople}
				/>
			)}
			<IconButton
				id="people-delete"
				label="Delete"
				icon={<MdDelete className="mr-2" />}
				className="secondary h-10"
				rounded
				onClick={() => openDeleteModal()}
			/>
		</div>
	);

	const endContent = selectedPeoples.length ? (selectedActionButtons) : (
		<Row className="gap-3">
			<Button
				id="people-export"
				label="Export"
				icon={<MdCloudDownload size={18} className="text-primary" />}
				pt={{
					root: { className: 'h-10 gap-2 rounded-[100px] pl-4 pr-6 bg-secondaryContainer' },
					label: { className: 'font-poppins text-label-large2 text-secondary' },
				}}
				onClick={() => downloadPeople(people.Items)}
			/>
			<Button
				id="people-bulk-add"
				label="Bulk Add"
				icon={<MdCloudUpload size={18} className="text-primary" />}
				pt={{
					root: { className: 'h-10 gap-2 rounded-[100px] pl-4 pr-6 bg-secondaryContainer' },
					label: { className: 'font-poppins text-label-large2 text-secondary' },
				}}
				onClick={() => {
					setAddType('bulk');
					setOpenAddModal(true);
				}}
			/>
			<Button
				id="people-add"
				label="Add"
				icon={<MdPersonAdd size={18} className="text-white" />}
				pt={{
					root: { className: 'h-10 gap-2 rounded-[100px] pl-4 pr-6 bg-primary' },
					label: { className: 'font-poppins text-label-large2 text-white' },
				}}
				onClick={() => {
					setAddType('single');
					setOpenAddModal(true);
				}}
			/>
		</Row>
	);

	return (
		<div>
			<Toolbar
				pt={{
					root: { className: 'people-toolbar mb-6' },
					start: { className: 'tablet:w-full' },
				}}
				start={startContent}
				end={isManagerOrAdmin ? endContent : (<></>)}
			/>
			<Table
				size="small"
				value={people.Items}
				emptyMessage={EmptyTable({
					title: 'No employees yet',
					description: 'You’ll able to see your employees here.',
					src: '/images/svg/Empty State - No Employees.svg',
				})}
				selectionMode={'checkbox'}
				onRowClick={navigate}
				selection={selectedPeoples}
				onSelectionChange={(e: DataTableValue) => setSelectedPeoples(e.value)}
				dataKey="Id"
				lazy
				paginator={hasPeople}
				totalRecords={people.TotalRecords}

				removableSort={true}
				rowsPerPageOptions={[5, 10, 25, 50]}
				{...tableProps}
			>
				<Column selectionMode="multiple" />
				<Column field="FullName" header="Name" sortable />
				<Column field="StatusName" header="Status" sortable body={statusBodyTemplate} />
				<Column field="Email" header="Email" sortable />
				<Column
					field="ReferenceCode"
					header="Employee ID"
					sortable
					headerClassName="tablet:!hidden"
					bodyClassName="tablet:!hidden"
				/>
				<Column field="RoleName" header="Role" sortable />
				<Column field="GroupName" header="Group" sortable />
				<Column field="LastInvited" header="Last Invited" sortable
					headerClassName="tablet:!hidden"
					bodyClassName="tablet:!hidden"
					body={(person) => {
						if (!person.lastInvited) return "";
						return formatDistanceToNowStrict(person.lastInvited, { addSuffix: true });
					}}
				/>
			</Table>
			<AddPeopleScreen
				visible={openAddModal}
				onHide={() => setOpenAddModal(false)}
				type={addType}
			/>
			{/* <EditPeopleDialog
				visible={openEditModal}
				employeeId={selectedPeoples[0]?.Id}
				onHide={() => {
					setOpenEditModal(false);
				}}
				type={editType}
			/> */}
		</div>
	);
};

export default ApprovedPeopleTable;
